import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from 'components/AppIcon';

export default function EmailConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location?.state?.email || '';

  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendCount, setResendCount] = useState(0);

  // Poll for email confirmation in background
  useEffect(() => {
    const interval = setInterval(async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user?.email_confirmed_at) {
        clearInterval(interval);
        navigate('/main-dashboard', { replace: true });
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [navigate]);

  const handleResend = async () => {
    if (!email) return;
    setResendLoading(true);
    setResendError('');
    setResendSuccess(false);
    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email,
      });
      if (error) {
        setResendError(error?.message || 'Error al reenviar. Inténtalo de nuevo.');
      } else {
        setResendSuccess(true);
        setResendCount(c => c + 1);
      }
    } catch (err) {
      setResendError('Error de red. Inténtalo de nuevo.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--color-background)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: 'var(--color-accent)' }}>
            <Icon name="Shield" size={28} color="white" strokeWidth={2} />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">MetalGuard</h1>
        </div>

        {/* Card */}
        <div className="card p-8 text-center">
          {/* Envelope icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ background: 'var(--color-accent)18' }}>
            <Icon name="Mail" size={40} color="var(--color-accent)" strokeWidth={1.5} />
          </div>

          <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Revisa tu email</h2>

          <p className="text-muted-foreground mb-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem', lineHeight: '1.6' }}>
            Te hemos enviado un enlace de confirmación a:
          </p>

          {email && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-4 border border-border" style={{ background: 'var(--color-muted)' }}>
              <Icon name="AtSign" size={14} color="var(--color-accent)" strokeWidth={2} />
              <span className="text-sm font-medium text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{email}</span>
            </div>
          )}

          <p className="text-muted-foreground mb-6" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', lineHeight: '1.6' }}>
            Confirma tu cuenta para poder iniciar sesión. Esta página se actualizará automáticamente cuando confirmes tu email.
          </p>

          {/* Resend section */}
          {resendSuccess ? (
            <div className="flex items-center justify-center gap-2 p-3 rounded-lg mb-4" style={{ background: 'var(--color-success)15' }}>
              <Icon name="CheckCircle" size={16} color="var(--color-success)" strokeWidth={2} />
              <span className="text-sm" style={{ color: 'var(--color-success)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Email reenviado correctamente.
              </span>
            </div>
          ) : resendError ? (
            <div className="flex items-center gap-2 p-3 rounded-lg mb-4 border" style={{ background: 'var(--color-error)15', borderColor: 'var(--color-error)40' }}>
              <Icon name="AlertCircle" size={16} color="var(--color-error)" strokeWidth={2} />
              <span className="text-sm" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{resendError}</span>
            </div>
          ) : null}

          <button
            onClick={handleResend}
            disabled={resendLoading || !email}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white font-semibold transition-opacity disabled:opacity-60 mb-4"
            style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}
          >
            {resendLoading ? (
              <><Icon name="Loader2" size={18} color="white" strokeWidth={2} className="animate-spin" /> Reenviando...</>
            ) : (
              <><Icon name="RefreshCw" size={18} color="white" strokeWidth={2} /> Reenviar email</>
            )}
          </button>

          <Link
            to="/login"
            className="text-sm font-medium"
            style={{ color: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
          >
            ← Volver al login
          </Link>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          ¿No ves el email? Revisa tu carpeta de spam.
        </p>
      </div>
    </div>
  );
}
